import { FormErrors } from "@/components/forms/form-errors";
import { useEffect, useRef } from "react";
import { useForm, usePage, useRemember } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Field, FieldLabel, FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { LocationMapPreview } from "@/components/location-map-preview";
import {
  WeeklyScheduleFields,
  weekDays,
} from "@/components/weekly-schedule-fields";
import { onboardingPath } from "@/routes";

export type OnboardingProps = {
  countries: {
    value: string;
    label: string;
    currency: string;
    time_zone: string;
  }[];
  time_zones: { value: string; label: string }[];
  initial_user: { first_name?: string; last_name?: string };
  maps_browser_key: string | null;
  trial: {
    days: number;
    plan_key: string;
    price: { amount_cents: number; interval: string; plan_key: string };
    currency: string;
  };
};
export function OnboardingForm({
  countries,
  time_zones: zones,
  initial_user: user,
  trial,
  maps_browser_key: mapsBrowserKey,
}: OnboardingProps) {
  const { t, i18n } = useTranslation();
  const { auth } = usePage().props;
  const rememberKey = `onboarding:${auth.account?.id}:${auth.user?.id}`;
  const [step, setStep] = useRemember(0, `${rememberKey}:step`);
  const heading = useRef<HTMLHeadingElement>(null);
  const form = useForm(rememberKey, {
    onboarding: {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      locale: "en",
      account_name: "",
      location_name: "",
      team_size: 1,
      address_line_1: "",
      address_line_2: "",
      city: "",
      region: "",
      postal_code: "",
      country_code: "US",
      currency: "USD",
      time_zone: "UTC",
      weekly_intervals: Object.fromEntries(
        weekDays.map((day) => [
          day,
          day === "saturday" || day === "sunday"
            ? []
            : [{ opens_at: "09:00", closes_at: "17:00" }],
        ]),
      ) as Record<string, { opens_at: string; closes_at: string }[]>,
    },
  });
  useEffect(() => {
    if (!Object.keys(form.errors).length)
      heading.current?.focus({ preventScroll: true });
  }, [step, form.errors]);
  const data = form.data.onboarding;
  const update = <K extends keyof typeof data>(
    key: K,
    value: (typeof data)[K],
  ) => form.setData("onboarding", { ...data, [key]: value });
  const fields =
    step === 0
      ? (["first_name", "last_name", "account_name", "team_size"] as const)
      : ([
          "location_name",
          "address_line_1",
          "address_line_2",
          "city",
          "region",
          "postal_code",
          "currency",
        ] as const);
  const next = () => {
    form.clearErrors();
    const missing =
      step === 0
        ? (["first_name", "account_name"] as const).filter(
            (key) => !data[key].trim(),
          )
        : (["location_name", "address_line_1", "city"] as const).filter(
            (key) => !data[key].trim(),
          );
    missing.forEach((key) =>
      form.setError(`onboarding.${key}`, [t("onboarding.required")]),
    );
    const invalidTeam =
      step === 0 && (!Number.isInteger(data.team_size) || data.team_size < 1);
    if (invalidTeam)
      form.setError("onboarding.team_size", [t("onboarding.required")]);
    if (!missing.length && !invalidTeam) setStep(step + 1);
  };
  return (
    <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
      <nav aria-label={t("access.setup_steps")}>
        <ol className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
          {["business", "location", "hours_review"].map((name, index) => (
            <li key={name}>
              <Button
                className="min-h-11 w-full justify-start whitespace-normal text-left"
                variant={step === index ? "secondary" : "ghost"}
                disabled={index > step}
                aria-current={step === index ? "step" : undefined}
                onClick={() => setStep(index)}
              >
                {index + 1}. {t(`access.${name}`)}
              </Button>
            </li>
          ))}
        </ol>
      </nav>
      <Card className="min-w-0">
        <CardHeader>
          <h2 ref={heading} tabIndex={-1} className="text-lg font-semibold">
            {t(`access.${["business", "location", "hours_review"][step]}`)}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("onboarding.step", { current: step + 1, total: 3 })}
          </p>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              if (step < 2) {
                next();
                return;
              }
              form.post(onboardingPath(), {
                onError: (errors) => {
                  const keys = Object.keys(errors);
                  setStep(
                    keys.includes("onboarding.weekly_intervals")
                      ? 2
                      : keys.some((key) =>
                            /location_name|address|city|region|postal_code|country_code|currency|time_zone|locale/.test(
                              key,
                            ),
                          )
                        ? 1
                        : 0,
                  );
                },
              });
            }}
          >
            <FormErrors
              errors={form.errors}
              targets={{
                ...Object.fromEntries(
                  Object.keys(data).map((key) => [`onboarding.${key}`, key]),
                ),
                "onboarding.country_code": "country",
                "onboarding.time_zone": "timezone",
                "onboarding.locale": "language",
                "onboarding.weekly_intervals": "onboarding-schedule",
              }}
            />
            {step < 2 &&
              fields.map((key) => (
                <Field key={key}>
                  <FieldLabel htmlFor={key}>
                    {t(
                      `${["first_name", "last_name", "account_name", "location_name", "team_size"].includes(key) ? "onboarding" : "fields"}.${key}`,
                    )}
                  </FieldLabel>
                  <Input
                    id={key}
                    name={`onboarding[${key}]`}
                    type={key === "team_size" ? "number" : "text"}
                    min={key === "team_size" ? 1 : undefined}
                    value={data[key]}
                    onChange={(event) =>
                      update(
                        key,
                        (key === "team_size"
                          ? Number(event.target.value)
                          : event.target.value) as never,
                      )
                    }
                  />
                  <FieldError>
                    {form.errors[
                      `onboarding.${key}` as keyof typeof form.errors
                    ] || form.errors[key as keyof typeof form.errors]}
                  </FieldError>
                </Field>
              ))}
            {step === 1 && (
              <Field>
                <FieldLabel htmlFor="language">
                  {t("common.language")}
                </FieldLabel>
                <NativeSelect
                  id="language"
                  value={data.locale}
                  onChange={(event) => update("locale", event.target.value)}
                >
                  <NativeSelectOption value="en">English</NativeSelectOption>
                  <NativeSelectOption value="ro">Română</NativeSelectOption>
                </NativeSelect>
                <FieldError>{form.errors["onboarding.locale"]}</FieldError>
              </Field>
            )}
            {step === 1 && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="country">
                    {t("fields.country_code")}
                  </FieldLabel>
                  <NativeSelect
                    id="country"
                    value={data.country_code}
                    onChange={(event) => {
                      const country = countries.find(
                        (item) => item.value === event.target.value,
                      );
                      form.setData("onboarding", {
                        ...data,
                        country_code: event.target.value,
                        currency: country?.currency || data.currency,
                        time_zone: country?.time_zone || data.time_zone,
                      });
                    }}
                  >
                    {countries.map((country) => (
                      <NativeSelectOption
                        key={country.value}
                        value={country.value}
                      >
                        {t(`countries.${country.value}`, {
                          defaultValue: country.label,
                        })}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
                <Field>
                  <FieldLabel htmlFor="timezone">
                    {t("fields.time_zone")}
                  </FieldLabel>
                  <NativeSelect
                    id="timezone"
                    value={data.time_zone}
                    onChange={(event) =>
                      update("time_zone", event.target.value)
                    }
                  >
                    {zones.map((zone) => (
                      <NativeSelectOption key={zone.value} value={zone.value}>
                        {zone.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
            )}
            {step === 1 && (
              <LocationMapPreview
                browserKey={mapsBrowserKey}
                address={[
                  data.address_line_1,
                  data.address_line_2,
                  data.city,
                  data.region,
                  data.postal_code,
                  data.country_code,
                ]
                  .filter(Boolean)
                  .join(", ")}
              />
            )}
            {step === 2 && (
              <>
                <section
                  className="space-y-3 rounded-lg border p-4"
                  aria-label={t("access.review_setup")}
                >
                  <h3 className="font-semibold">{t("access.review_setup")}</h3>
                  <dl className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-muted-foreground text-sm">
                        {t("access.business")}
                      </dt>
                      <dd>
                        {data.account_name} · {data.first_name} {data.last_name}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-sm">
                        {t("access.location")}
                      </dt>
                      <dd>
                        {data.location_name}
                        <br />
                        {[data.address_line_1, data.city, data.country_code]
                          .filter(Boolean)
                          .join(", ")}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-sm">
                        {t("common.language")}
                      </dt>
                      <dd>{data.locale === "ro" ? "Română" : "English"}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground text-sm">
                        {t("fields.time_zone")}
                      </dt>
                      <dd>
                        {data.time_zone} · {data.currency}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-muted-foreground text-sm">
                    {t("access.checkout_help")}
                  </p>
                </section>
                <p>
                  {t("onboarding.trial_notice", {
                    days: trial.days,
                    plan: t(`billing_ui.plans.${trial.plan_key}.name`),
                    post_plan: t(
                      `billing_ui.plans.${trial.price.plan_key}.name`,
                    ),
                    amount: new Intl.NumberFormat(i18n.language, {
                      style: "currency",
                      currency: trial.currency.toUpperCase(),
                      currencyDisplay: "code",
                    }).format(trial.price.amount_cents / 100),
                    interval: t(`billing_ui.${trial.price.interval}`),
                  })}
                </p>
                <section
                  id="onboarding-schedule"
                  tabIndex={-1}
                  aria-label={t("common.schedule")}
                  className="outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <WeeklyScheduleFields
                    schedule={data.weekly_intervals}
                    onChange={(value) => update("weekly_intervals", value)}
                  />
                </section>
              </>
            )}
            {Object.entries(form.errors)
              .filter(
                ([key]) =>
                  step === 2 ||
                  !fields.some(
                    (field) => key === `onboarding.${field}` || key === field,
                  ),
              )
              .map(([key, error]) => (
                <FieldError key={key}>
                  {Array.isArray(error) ? error.join(" ") : error}
                </FieldError>
              ))}
            <div className="flex gap-3">
              {step > 0 && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setStep(step - 1)}
                >
                  {t("common.back")}
                </Button>
              )}
              {step < 2 ? (
                <Button
                  key="next"
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    next();
                  }}
                >
                  {t("common.continue")}
                </Button>
              ) : (
                <Button key="checkout" type="submit" disabled={form.processing}>
                  {t("onboarding.checkout")}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
